import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet, RouteObject, useMatches } from 'react-router-dom';
import { NavConfig } from '../../data/constants/nav';
import { NavLinkId } from '../../data/types/nav';
import cepRoutes from './cepRoutes';
import communicationRoutes from './communicationRoutes';
import ebisPrintRoutes from './ebisPrintRoutes';
import empRoutes from './empRoutes';
import fullScreenRoutes from './fullScreenRoutes';
import intlRoutes from './intlRoutes';
import loginRoutes from './loginRoutes';
import mjcRoutes from './mjcRoutes';
import mncRoutes from './mncRoutes';
import profileRoutes from './profileRoutes';
import r4DomAmaRoutes from './r4DomAmaRoutes';
import r4Routes from './r4Routes';
import r5Routes from './r5Routes';
import reportRoutes from './reportRoutes';
import rosterRoutes from './rosterRoutes';
import systemRoutes from './systemRoutes';
import transactionRoutes from './transactionRoutes';
import txRoutes from './txRoutes';
import utilRoutes from './utilRoutes';

const GlobalProviders = lazy(() => import('../GlobalProviders'));
const EbisAppFrame = lazy(() => import('../frames/EbisAppFrame'));
const AdvSearch = lazy(() => import('../adv-search/AdvSearch'));
const ClipboardMain = lazy(() => import('../clipboard/ClipboardMain'));
const Clipboard = lazy(() => import('../clipboard/Clipboard'));

const Esignature = lazy(() => import('../esignature/Esignature'));

const AwardBonusPackage = lazy(
  () => import('../major-contracts/MjAwardBonusPackages/AwardBonusPackage')
);
const AffiliateProfile = lazy(
  () => import('../utilities/affiliates/AffiliateProfile')
);

const DocumentTitleUpdater = (): null => {
  const matches = useMatches();

  const title = useMemo(() => {
    const match = matches
      .reverse()
      .find(({ id }) => !!NavConfig.linkConfigs[id as NavLinkId]?.label);
    return NavConfig.linkConfigs[match?.id as NavLinkId]?.label ?? 'Ebis';
  }, [matches]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  return null;
};

const routes: RouteObject[] = [
  ...loginRoutes,
  {
    element: (
      <Suspense>
        <GlobalProviders>
          <Suspense>
            <Outlet />
          </Suspense>
        </GlobalProviders>
      </Suspense>
    ),
    children: [
      ...ebisPrintRoutes,
      ...fullScreenRoutes,
      {
        element: (
          <>
            <DocumentTitleUpdater />
            <EbisAppFrame>
              <ErrorBoundary fallback={<div />}>
                <Suspense>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </EbisAppFrame>
          </>
        ),
        children: [
          ...rosterRoutes,
          ...mjcRoutes,
          {
            id: NavConfig.linkConfigs.AWARD_BONUS_PACKAGE.id,
            path: NavConfig.linkConfigs.AWARD_BONUS_PACKAGE.to,
            element: <AwardBonusPackage />,
          },
          ...mncRoutes,
          ...intlRoutes,
          ...txRoutes,
          ...utilRoutes,
          ...systemRoutes,
          ...empRoutes,
          ...communicationRoutes,
          {
            id: NavConfig.linkConfigs.ADVANCED_SEARCH.id,
            path: NavConfig.linkConfigs.ADVANCED_SEARCH.to,
            element: <AdvSearch />,
          },
          {
            id: NavConfig.linkConfigs.E_SIGNATURE.id,
            path: NavConfig.linkConfigs.E_SIGNATURE.to,
            element: <Esignature />,
          },
          {
            id: NavConfig.linkConfigs.CLIPBOARD.id,
            path: NavConfig.linkConfigs.CLIPBOARD.to,
            element: (
              <ClipboardMain>
                <Suspense>
                  <Outlet />
                </Suspense>
              </ClipboardMain>
            ),
            children: [
              {
                index: true,
                element: <Clipboard />,
              },
            ],
          },
          ...cepRoutes,
          {
            id: NavConfig.linkConfigs.AFFILIATE_PROFILE.id,
            path: NavConfig.linkConfigs.AFFILIATE_PROFILE.to,
            element: <AffiliateProfile />,
          },
          ...profileRoutes,
          ...r4DomAmaRoutes,
          ...r4Routes,
          ...reportRoutes,
          ...r5Routes,
          ...transactionRoutes,
        ],
      },
    ],
  },
];

export default routes;
